const EXPIRED_PREPARATION_ALERT_WINDOW_MS = 7 * 24 * 60 * 60 * 1_000;

export interface ExpirationAlertCandidate {
  id: string;
  foodId: string;
  expirationDate: string | null;
  isPreparation: boolean;
}

function expirationTime(candidate: ExpirationAlertCandidate): number {
  if (!candidate.expirationDate) return Number.NaN;
  return Date.parse(candidate.expirationDate);
}

/**
 * Keeps inventory expiry alerts actionable without changing append-only stock.
 *
 * Purchased stock remains visible until it is physically discharged. Produced
 * preparations are different: once expired, only the newest expired lot for
 * each preparation is shown, and only for seven days after its expiry.
 */
export function selectActionableExpirationAlerts<T extends ExpirationAlertCandidate>(
  candidates: readonly T[],
  now = Date.now(),
): T[] {
  const latestExpiredPreparationByFood = new Map<string, T>();

  for (const candidate of candidates) {
    const expiresAt = expirationTime(candidate);
    if (
      !candidate.isPreparation
      || !Number.isFinite(expiresAt)
      || expiresAt >= now
      || now - expiresAt >= EXPIRED_PREPARATION_ALERT_WINDOW_MS
    ) {
      continue;
    }

    const current = latestExpiredPreparationByFood.get(candidate.foodId);
    if (!current) {
      latestExpiredPreparationByFood.set(candidate.foodId, candidate);
      continue;
    }
    const currentExpiresAt = expirationTime(current);
    if (
      expiresAt > currentExpiresAt
      || (expiresAt === currentExpiresAt && candidate.id > current.id)
    ) {
      latestExpiredPreparationByFood.set(candidate.foodId, candidate);
    }
  }

  const visibleExpiredPreparationIds = new Set(
    [...latestExpiredPreparationByFood.values()].map((candidate) => candidate.id),
  );

  return candidates.filter((candidate) => {
    if (!candidate.isPreparation) return true;
    const expiresAt = expirationTime(candidate);
    if (!Number.isFinite(expiresAt) || expiresAt >= now) return true;
    return visibleExpiredPreparationIds.has(candidate.id);
  });
}
